import React, { useEffect, useState } from "react";
import { FaSort, FaSortUp, FaSortDown, FaComments, FaTimes, FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import Toast from "../../components/Toast/Toast";
import { getSupportTicketMessageCountAPI, getSupportTicketsAPI, updateSupportTicketAPI } from "../../utils/api";
import Sidebar from "../Sidebar/Sidebar";
import TicketDetailsModal from "./components/TicketDetailsModal";
import "./SupportTicket.css";
import { useDispatch } from "react-redux";
import { setNewTicketOrReply } from "../../store/slices/supportTicketState";

const PAGE_LIMIT = 10;

const SupportTicket = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Sorting state
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  const [pendingClose, setPendingClose] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetails, setShowTicketDetails] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError("");
      const messageCountResponse = await getSupportTicketMessageCountAPI();
      if(messageCountResponse?.status === 200&&(messageCountResponse?.message_count>0||messageCountResponse?.ticket_count>0)){
        dispatch(setNewTicketOrReply(1));
      }else{
        dispatch(setNewTicketOrReply(0));
      }
      const response = await getSupportTicketsAPI({
        page,
        limit: PAGE_LIMIT,
        search: searchTerm.trim() || "",
        search_by: "user_name", // Only search by user name
        status: statusFilter || "", // Add status filter
      });

      if (response?.status === 200) {
        const list = response.data;
        const totalPagesFromApi = response?.pagination?.total_pages || 1;
        setTickets(list);
        setTotalPages(totalPagesFromApi);
      } else {
        setTickets([]);
        setTotalPages(1);
        setError(response?.message || "Failed to fetch support tickets");
      }
    } catch (err) {
      setTickets([]);
      setTotalPages(1);
      setError("Failed to fetch support tickets. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    fetchTickets();
  }, [page, searchTerm, statusFilter]);

  const startIdx = (page - 1) * PAGE_LIMIT;
  const endIdx = startIdx + tickets.length;

  const handleCloseTicket = (ticketId) => {
    setPendingClose(ticketId);
  };

  const handleConfirmClose = async () => {
    if (!pendingClose) return;
    try {
      const reqData = { 
        support_ticket_id: pendingClose, 
        status: "closed" 
      };
      const response = await updateSupportTicketAPI(reqData);
      console.log(response);
      if (response?.status === 200) {
        showToast("Ticket closed successfully");
        fetchTickets(); // Fetch all tickets again to get updated data
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast("Failed to close ticket", "error");
    } finally {
      setPendingClose(null);
    }
  };

  const handleCancelClose = () => setPendingClose(null);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleChatClick = (ticketId) => {
    navigate('/support-chat', { state: { ticket_id: ticketId } });
  };

  const handleDescriptionClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetails(true);
  };

  const handleCloseTicketDetails = () => {
    setShowTicketDetails(false);
    setSelectedTicket(null);
  };

  // Sorting functionality
  const handleSort = (field) => {
    let newOrder = "asc";
    if (sortField === field) {
      newOrder = sortOrder === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortOrder(newOrder);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <FaSort />;
    }
    return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const getSortedData = () => {
    if (!sortField || !sortOrder) {
      return tickets;
    }

    return [...tickets].sort((a, b) => {
      let aValue, bValue;

      // Handle nested user.name field
      if (sortField === "user.name") {
        aValue = a?.user?.name || "";
        bValue = b?.user?.name || "";
      } else {
        aValue = a[sortField] || "";
        bValue = b[sortField] || "";
      }

      // Convert to lowercase for case-insensitive sorting
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();

      if (sortOrder === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  // Truncate text function
  const truncateText = (text, maxLength = 50) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Get sorted data for display
  const sortedTickets = getSortedData();

  return (
    <>
      <Toast toast={toast} />
      <TicketDetailsModal
        isVisible={showTicketDetails}
        onClose={handleCloseTicketDetails}
        ticketData={selectedTicket}
      />
      <div className="SupportTicket-layout">
        <Sidebar />
        <div className="SupportTicket-main-content">
          <div className="SupportTicket-header-row">
            <div className="filter-container">
              <input
                type="text"
                className="search-input"
                placeholder="Search by user name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search support tickets"
              />
              <select
                className="status-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading tickets...</p>
            </div>
          ) : error ? (
            <div className="loading-container">
              <div style={{ color: "#dc2626", fontSize: "1.25rem", marginBottom: "1rem" }}>⚠️</div>
              <p style={{ color: "#dc2626", fontSize: "1.125rem" }}>{error}</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="empty-state">
              <h3>No Support Tickets Found</h3>
              <p>There are no support tickets in the system yet.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="SupportTicket-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th 
                        className="sortable-header"
                        // onClick={() => handleSort("user.name")}
                      >
                        <div className="header-content">
                          <span>USER</span>
                          {/* <span className="sort-icon">{getSortIcon("user.name")}</span> */}
                        </div>
                      </th>
                      <th>SUBJECT</th>
                      <th>DESCRIPTION</th>
                      <th>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket, idx) => (
                      <tr key={ticket?._id || `ticket-${idx}`}>
                        <td>{startIdx + idx + 1}</td>
                        <td>{ticket?.user?.name || "Unknown User"}</td>
                        <td title={ticket?.subject || ""}>{truncateText(ticket?.subject || "", 30)}</td>
                        <td>{truncateText(ticket?.description || "", 50)}</td>
                        <td>
                          <div className="support-action-buttons">
                            <button
                              className="support-action-btn support-view-btn"
                              onClick={() => handleDescriptionClick(ticket)}
                              title="View Details"
                            >
                              <FaEye />
                            </button>
                            {ticket?.status === "open" && (
                              <>
                                <div className="chat-button-container">
                                  <button
                                    className="support-action-btn support-chat-btn"
                                    onClick={() => handleChatClick(ticket?._id)}
                                    title="Chat"
                                  >
                                    <FaComments />
                                    {ticket?.unread_count > 0 && (
                                      <span className="unread-dot"></span>
                                    )}
                                  </button>
                                </div>
                                <button
                                  className="support-action-btn support-close-btn"
                                  onClick={() => handleCloseTicket(ticket?._id)}
                                  title="Close Ticket"
                                >
                                  <FaTimes />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-footer">
                <div className="footer-left">
                  <span>
                    Showing {tickets.length > 0 ? startIdx + 1 : 0} to {endIdx} • Page {page} of {totalPages}
                  </span>
                </div>
                <div className="pagination">
                  <button type="button" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>
                    Previous
                  </button>
                  {[...Array(totalPages).keys()].slice(0, 5).map((i) => (
                    <button
                      type="button"
                      key={i + 1}
                      className={page === i + 1 ? "active" : ""}
                      onClick={() => handlePageChange(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  {totalPages > 5 && <span className="ellipsis">…</span>}
                  <button type="button" disabled={page === totalPages} onClick={() => handlePageChange(page + 1)}>
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmationModal
        visible={!!pendingClose}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        title="Close Ticket"
        message="Are you sure you want to close this support ticket?"
        confirmText="Close"
      />
    </>
  );
};

export default SupportTicket; 